/**
 * wishlist-collection.js
 * Wires up <wishlist-heart> custom elements rendered by SearchSpring Snap
 * (and anywhere else, e.g. mega menu, carousels) to WishlistAPI.
 *
 * FIX: clicks are handled via a single delegated listener on document,
 * so they keep working even when buttons are cloned/re-rendered by a
 * carousel (Slick/Splide infinite-loop clones) or a mega menu that
 * rebuilds its markup from a <template> on open/hover. Per-element
 * initHeart() is now only responsible for the *visual saved state*
 * (heart fill) and its live-update subscription — it is no longer a
 * requirement for the click to work.
 */
(function () {

  // ── CSS ───────────────────────────────────────────────────────────────────
  var style = document.createElement('style');
  style.textContent = `
    @media only screen and (max-width: 768px) {
      .wl-heart-btn .btn--circle { line-height: 0; }
      .wl-heart-btn .icon { width: 12px !important; height: 12px !important; }
    }
    /* Default — outline only */
    .wl-heart-btn .wl-heart-fill  { opacity: 0; }
    .wl-heart-btn .wl-heart-stroke { opacity: 1; }

    /* Saved — filled */
    .wl-heart-btn.is-saved .wl-heart-fill  { opacity: 1; color: #004350; }
    .wl-heart-btn.is-saved .wl-heart-stroke { opacity: 0; }
  `;
  document.head.appendChild(style);

  // ── Wait for WishlistAPI ──────────────────────────────────────────────────
  function waitForAPI(callback, attempts) {
    attempts = attempts || 0;
    if (window.WishlistAPI) {
      callback(window.WishlistAPI);
    } else if (attempts < 100) {
      setTimeout(function () { waitForAPI(callback, attempts + 1); }, 50);
    } else {
      console.warn('[WishlistHeart] WishlistAPI not available');
    }
  }

  // ── Build product data object from <wishlist-heart> data attributes ───────
  function getProductData(el) {
    var productId = el.dataset.productId || '';
    var variantId = el.dataset.variantId || '';

    return {
      productId:    productId.startsWith('gid://')
                      ? productId
                      : 'gid://shopify/Product/' + productId,
      variantId:    variantId
                      ? (variantId.startsWith('gid://')
                          ? variantId
                          : 'gid://shopify/ProductVariant/' + variantId)
                      : null,
      productTitle:  el.dataset.productTitle ? el.dataset.productTitle : el.dataset.vendor + " " + el.dataset.sku || '',
      productHandle: el.dataset.productHandle || '',
      productImage:  el.dataset.productImage  || '',
      price: el.dataset.wlPrice ? formatMoney(el.dataset.wlPrice) : null,
      compareAtPrice: el.dataset.comparePrice || null,
      variantSku:    el.dataset.sku           || null,
      productVendor: el.dataset.vendor        || null,
      productType:   el.dataset.producttype   || null,
      aslsku:        el.dataset.aslsku        || null,
      variantTitle:  el.dataset.finish        || '',
      variantAvailable: el.dataset.variantAvailable || null,
      quantity: 1,
      pickupOnly: el.dataset.pickupOnly || false,
    };
  }

  // ── Update button appearance based on saved state ─────────────────────────
  function updateBtn(btn, isSaved) {
    btn.classList.toggle('is-saved', isSaved);
    btn.setAttribute('aria-label', isSaved ? 'Saved to wishlist' : 'Save to wishlist');
    btn.setAttribute('title',      isSaved ? 'Saved to wishlist' : 'Save to wishlist');
  }

  const isEmpty = (val) => val == null || val.toString().trim() === "";

  function formatMoney(amount) {
    try {
      const numeric = parseFloat(String(amount).replace(/,/g, ''));
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(parseFloat(numeric));
    } catch (e) {
      const numeric = parseFloat(String(amount).replace(/,/g, ''));
      return 'CAD ' + numeric.toFixed(2);
    }
  }

  // ── Resolve the wishlist-heart wrapper + button from any click target ─────
  function resolveHeart(target) {
    var btn = target.closest('.wl-heart-btn');
    if (!btn) return null;
    var heartEl = btn.closest('wishlist-heart');
    if (!heartEl) return null;
    return { btn: btn, heartEl: heartEl };
  }

  // ── Per-element setup: initial saved-state + live subscription only ───────
  // (No longer responsible for click — see delegated listener below.)
  function initHeart(heartEl, api) {
    if (heartEl._wlInit) return;
    heartEl._wlInit = true;

    var btn = heartEl.querySelector('.wl-heart-btn');
    if (!btn) return;

    var pd = getProductData(heartEl);
    var fullProductId = pd.productId;

    updateBtn(btn, api.listsContainingProduct(fullProductId).length > 0);

    var unsub = api.subscribe(function () {
      updateBtn(btn, api.listsContainingProduct(fullProductId).length > 0);
    });

    var observer = new MutationObserver(function () {
      if (!document.contains(heartEl)) {
        unsub && unsub();
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Simple list picker dropdown for multiple lists ────────────────────────
  function openPicker(heartEl, btn, pd, api) {
    var existing = document.getElementById('wl-collection-picker');
    if (existing) existing.remove();

    var picker = document.createElement('div');
    picker.id = 'wl-collection-picker';
    picker.style.cssText = `
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      background: #fff;
      border: 1px solid #e8e3dc;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
      z-index: 1000;
      min-width: 180px;
      overflow: hidden;
      animation: wlPickerIn 0.15s ease;
    `;

    if (!document.getElementById('wl-picker-style')) {
      var s = document.createElement('style');
      s.id = 'wl-picker-style';
      s.textContent = '@keyframes wlPickerIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}';
      document.head.appendChild(s);
    }

    var inLists = api.listsContainingProduct(pd.productId);

    api.state.lists.forEach(function (list) {
      var checked = inLists.some(function (l) { return l.id === list.id; });
      var opt = document.createElement('button');
      opt.style.cssText = `
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 10px 14px;
        border: none;
        background: ${checked ? '#fff5f3' : '#fff'};
        text-align: left;
        cursor: pointer;
        font-size: 13px;
        font-family: inherit;
        color: #1a1a1a;
      `;
      opt.innerHTML =
        '<span>' + list.emoji + ' ' + list.name + '</span>' +
        (checked ? '<span style="margin-left:auto;color:#c0503c;">✓</span>' : '');

      opt.addEventListener('click', async function (e) {
        e.stopPropagation();
        await api.toggleItem(list.id, pd);
        picker.remove();
      });

      picker.appendChild(opt);
    });

    heartEl.style.position = 'relative';
    heartEl.appendChild(picker);

    setTimeout(function () {
      document.addEventListener('click', function closePicker(e) {
        if (!picker.contains(e.target)) {
          picker.remove();
          document.removeEventListener('click', closePicker);
        }
      });
    }, 0);
  }

  // ── Init all <wishlist-heart> elements currently in the DOM ────────────────
  function initAllHearts(api) {
    var hearts = document.querySelectorAll('wishlist-heart:not([data-wl-init])');
    hearts.forEach(function (el) {
      el.setAttribute('data-wl-init', 'true');
      initHeart(el, api);
    });
  }

  // ── MutationObserver — catches SS re-renders, filters, infinite scroll,
  //    carousel clones, mega-menu re-renders, template stamping, etc. ───────
  function startObserver(api) {
    var observer = new MutationObserver(function (mutations) {
      var hasNew = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && (
            n.tagName === 'WISHLIST-HEART' ||
            (n.querySelector && n.querySelector('wishlist-heart'))
          );
        });
      });
      if (hasNew) initAllHearts(api);
    });

    // Just observe the whole document — cheap (childList/subtree only,
    // no attribute/characterData churn) and guarantees we never miss a
    // container because of a guessed selector that doesn't match this
    // theme's markup (grid, mega menu, wherever).
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // ── Delegated click handler — this is the actual fix ───────────────────────
  // Bound once on document, so it fires for ANY .wl-heart-btn that exists
  // at click time, regardless of whether it was cloned by a carousel,
  // stamped from a mega-menu <template>, or added after our observer's
  // debounce/timing. No dependency on initHeart() having run first.
  function bindDelegatedClick(api) {
    document.addEventListener('click', async function (e) {
      var resolved = resolveHeart(e.target);
      if (!resolved) return;

      var btn = resolved.btn;
      var heartEl = resolved.heartEl;

      e.preventDefault();
      e.stopPropagation();

      if (btn.classList.contains('adding')) return;
      btn.classList.add('adding');

      try {
        var pd = getProductData(heartEl);

        if (!api.state.lists || api.state.lists.length === 0) {
          await api.ensureDefaultList();
        }
        var lists = api.state.lists;

        if (lists.length === 1) {
          await api.toggleItem(lists[0].id, pd);
          return;
        }

        openPicker(heartEl, btn, pd, api);

      } catch (err) {
        console.warn('[WishlistHeart] toggle failed', err);
      } finally {
        btn.classList.remove('adding');
      }
    });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  waitForAPI(function (api) {
    console.log('pp');
    initAllHearts(api);
    startObserver(api);
    bindDelegatedClick(api);

    // Keep these — harmless belt-and-suspenders for state/visuals,
    // click no longer depends on them.
    document.addEventListener('ss__content',          function () { initAllHearts(api); });
    document.addEventListener('ss-render-pagination', function () { initAllHearts(api); });
    document.addEventListener('ss-infinite-scroll',   function () { initAllHearts(api); });
    document.addEventListener('main-content',         function () { initAllHearts(api); });
  });

})();
